import React, { useCallback, useEffect, useMemo, useState } from 'react'
import CustomerMemoRichEditor, { memoContentToHtml } from './CustomerMemoRichEditor'
import { api, getApiErrorMessage } from '../lib/api'
import {
  CUSTOMER_MEMO_TEMPLATE_VARIABLE_TOKENS,
  analyzeCustomerMemoTemplateVariables,
  applyCustomerMemoTemplateVariables,
  buildCustomerMemoTemplateVariables,
} from '../lib/customerMemoTemplates'

const STATUS_OPTIONS = [
  { value: 'PUBLISHED', label: '발행' },
  { value: 'DRAFT', label: '초안' },
  { value: 'HIDDEN', label: '숨김' },
]

const SCOPE_OPTIONS = [
  { value: 'GLOBAL', label: '전체' },
  { value: 'ORG', label: '조직' },
  { value: 'USER', label: '개인' },
]

function unwrap(res) {
  const body = res?.data
  if (body && Object.prototype.hasOwnProperty.call(body, 'data')) return body.data
  return body
}

function parseTags(value) {
  return String(value || '')
    .replaceAll(String.fromCharCode(13), ',')
    .replaceAll(String.fromCharCode(10), ',')
    .split(RegExp('[#,]+'))
    .map((item) => item.trim())
    .filter(Boolean)
    .filter((item, index, arr) => arr.findIndex((candidate) => candidate.toLowerCase() === item.toLowerCase()) === index)
    .slice(0, 12)
}

function tagsToInput(tags) {
  return Array.isArray(tags) ? tags.filter(Boolean).join(', ') : ''
}

function normalizeStatus(value, active) {
  const status = String(value || '').trim().toUpperCase()
  if (STATUS_OPTIONS.some((item) => item.value === status)) return status
  return active === false ? 'HIDDEN' : 'PUBLISHED'
}

function normalizeScope(value) {
  const scope = String(value || '').trim().toUpperCase()
  return SCOPE_OPTIONS.some((item) => item.value === scope) ? scope : 'GLOBAL'
}

function labelOf(options, value) {
  return options.find((item) => item.value === value)?.label || value
}

function templatePayload({ selectedRow, title, summary, plainText, content, status, sortOrder, category, tags, scopeType, scopeOrgId }) {
  const normalizedStatus = normalizeStatus(status)
  const normalizedScope = normalizeScope(scopeType)
  return {
    title: String(title || '').trim() || null,
    summary: String(summary || '').trim() || null,
    plainText: plainText || '',
    content: content || {
      schemaVersion: 1,
      blocks: String(plainText || '').trim() ? [{ type: 'paragraph', text: plainText }] : [],
    },
    active: normalizedStatus === 'PUBLISHED',
    status: normalizedStatus,
    version: selectedRow?.version ?? null,
    scopeType: normalizedScope,
    scopeOrgId: normalizedScope === 'ORG' && scopeOrgId ? Number(scopeOrgId) : null,
    sortOrder: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
    category: String(category || '').trim() || null,
    tags: parseTags(tags),
  }
}

function sortTemplates(rows) {
  const statusWeight = { PUBLISHED: 0, DRAFT: 1, HIDDEN: 2 }
  return [...(Array.isArray(rows) ? rows : [])].sort((a, b) => (statusWeight[normalizeStatus(a.status, a.active)] ?? 9) - (statusWeight[normalizeStatus(b.status, b.active)] ?? 9)
    || Number(a.sortOrder || 0) - Number(b.sortOrder || 0)
    || String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')))
}

function formatDate(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function formatBytes(value) {
  const n = Number(value || 0)
  if (!Number.isFinite(n) || n <= 0) return '0B'
  const units = ['B', 'KB', 'MB', 'GB']
  let size = n
  let unit = 0
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024
    unit += 1
  }
  const fixed = unit === 0 || size >= 10 ? Math.round(size) : size.toFixed(1)
  return `${fixed}${units[unit]}`
}

function showToast(message, type = 'success') {
  try { window?.toast?.show?.(message, type) } catch { }
}

export default function CustomerMemoTemplateManager({ open = true, onClose, onChanged, variant = 'modal', showClose = true }) {
  const [rows, setRows] = useState([])
  const [categories, setCategories] = useState([])
  const [usage, setUsage] = useState({ templates: [], recent: [] })
  const [memoOps, setMemoOps] = useState(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [title, setTitle] = useState('')
  const [summary, setSummary] = useState('')
  const [category, setCategory] = useState('')
  const [tags, setTags] = useState('')
  const [plainText, setPlainText] = useState('')
  const [content, setContent] = useState(null)
  const [status, setStatus] = useState('DRAFT')
  const [scopeType, setScopeType] = useState('GLOBAL')
  const [scopeOrgId, setScopeOrgId] = useState('')
  const [sortOrder, setSortOrder] = useState('0')
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [previewCustomer, setPreviewCustomer] = useState('홍길동')
  const [previewPhone, setPreviewPhone] = useState('010-0000-0000')
  const [previewUser, setPreviewUser] = useState('담당자')
  const [categoryEditingId, setCategoryEditingId] = useState(null)
  const [categoryName, setCategoryName] = useState('')
  const [categoryOrder, setCategoryOrder] = useState('0')
  const [categoryActive, setCategoryActive] = useState(true)

  const selectedRow = useMemo(() => rows.find((row) => String(row.id || '') === String(editingId || '')) || null, [editingId, rows])
  const statsById = useMemo(() => new Map((usage.templates || []).map((row) => [String(row.templateId), row])), [usage.templates])

  const clearEditor = useCallback(() => {
    setEditingId(null)
    setTitle('')
    setSummary('')
    setCategory('')
    setTags('')
    setPlainText('')
    setContent(null)
    setStatus('DRAFT')
    setScopeType('GLOBAL')
    setScopeOrgId('')
    setSortOrder('0')
  }, [])

  const selectTemplate = useCallback((template) => {
    if (!template) {
      clearEditor()
      return
    }
    setEditingId(template.id)
    setTitle(template.title || '')
    setSummary(template.summary || '')
    setCategory(template.category || '')
    setTags(tagsToInput(template.tags))
    setPlainText(template.plainText || '')
    setContent(template.content || null)
    setStatus(normalizeStatus(template.status, template.active))
    setScopeType(normalizeScope(template.scopeType))
    setScopeOrgId(template.scopeOrgId ? String(template.scopeOrgId) : '')
    setSortOrder(String(template.sortOrder ?? 0))
  }, [clearEditor])

  const loadTemplates = useCallback(async ({ keepSelection = true } = {}) => {
    const res = await api.get('/customer-management/customer-memo-templates/admin', { __silent: true })
    const nextRows = sortTemplates(Array.isArray(unwrap(res)) ? unwrap(res) : [])
    setRows(nextRows)
    if (!keepSelection) clearEditor()
    else if (editingId && !nextRows.some((row) => String(row.id) === String(editingId))) clearEditor()
    return nextRows
  }, [clearEditor, editingId])

  const loadCategories = useCallback(async () => {
    const res = await api.get('/customer-management/customer-memo-template-categories/admin', { __silent: true })
    const rows = Array.isArray(unwrap(res)) ? unwrap(res) : []
    setCategories(rows)
    return rows
  }, [])

  const loadUsage = useCallback(async () => {
    const res = await api.get('/customer-management/customer-memo-templates/admin/usage', { __silent: true })
    const body = unwrap(res) || {}
    setUsage({ templates: Array.isArray(body.templates) ? body.templates : [], recent: Array.isArray(body.recent) ? body.recent : [] })
  }, [])

  const loadOpsStats = useCallback(async () => {
    const res = await api.get('/customer-management/customer-memos/admin/stats', { __silent: true })
    setMemoOps(unwrap(res) || null)
  }, [])

  const loadAll = useCallback(async ({ keepSelection = true } = {}) => {
    setLoading(true)
    setError('')
    try {
      const [nextRows] = await Promise.all([loadTemplates({ keepSelection }), loadCategories(), loadUsage(), loadOpsStats()])
      return nextRows
    } catch (e) {
      const msg = getApiErrorMessage(e, '템플릿을 불러오지 못했습니다.')
      setError(msg)
      return []
    } finally {
      setLoading(false)
    }
  }, [loadCategories, loadOpsStats, loadTemplates, loadUsage])

  useEffect(() => {
    if (!open) return
    loadAll()
  }, [loadAll, open])

  const categoryOptions = useMemo(() => {
    const names = new Set(categories.filter((row) => row.active !== false).map((row) => String(row.name || '').trim()).filter(Boolean))
    rows.forEach((row) => { if (row.category) names.add(String(row.category)) })
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'ko-KR'))
  }, [categories, rows])

  const visibleRows = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return rows.filter((row) => {
      const rowStatus = normalizeStatus(row.status, row.active)
      if (statusFilter && rowStatus !== statusFilter) return false
      if (categoryFilter && String(row.category || '') !== categoryFilter) return false
      if (!needle) return true
      const haystack = [row.title, row.summary, row.category, row.plainText, ...(Array.isArray(row.tags) ? row.tags : [])].join(' ').toLowerCase()
      return haystack.includes(needle)
    })
  }, [categoryFilter, query, rows, statusFilter])

  const previewVariables = useMemo(() => buildCustomerMemoTemplateVariables({
    customer: { customerName: previewCustomer, customerPhone: previewPhone, companyName: '설계EZ 고객사' },
    user: { name: previewUser, companyName: '설계EZ' },
  }), [previewCustomer, previewPhone, previewUser])
  const draftTemplate = useMemo(() => ({ title, plainText, content }), [content, plainText, title])
  const previewTemplate = useMemo(() => applyCustomerMemoTemplateVariables(draftTemplate, previewVariables), [draftTemplate, previewVariables])
  const variableAnalysis = useMemo(() => analyzeCustomerMemoTemplateVariables(draftTemplate, previewVariables), [draftTemplate, previewVariables])

  const saveTemplate = async () => {
    if (!title.trim() && !plainText.trim()) {
      showToast('템플릿 제목 또는 내용을 입력해 주세요.', 'error')
      return
    }
    setSaving(true)
    setError('')
    try {
      const body = templatePayload({ selectedRow, title, summary, plainText, content, status, sortOrder, category, tags, scopeType, scopeOrgId })
      const res = editingId
        ? await api.patch(`/customer-management/customer-memo-templates/${editingId}`, body, { __silent: true })
        : await api.post('/customer-management/customer-memo-templates', body, { __silent: true })
      const saved = unwrap(res) || {}
      setEditingId(saved.id || editingId)
      showToast('템플릿이 저장되었습니다.', 'success')
      await Promise.resolve(onChanged?.())
      const nextRows = await loadAll({ keepSelection: true })
      const selected = nextRows.find((row) => String(row.id) === String(saved.id || editingId))
      if (selected) selectTemplate(selected)
    } catch (e) {
      const msg = getApiErrorMessage(e, '템플릿 저장에 실패했습니다.')
      setError(msg)
      showToast(msg, 'error')
    } finally {
      setSaving(false)
    }
  }

  const cloneTemplate = async () => {
    if (!editingId) return
    setSaving(true)
    setError('')
    try {
      const res = await api.post(`/customer-management/customer-memo-templates/${editingId}/clone`, null, { __silent: true })
      const cloned = unwrap(res) || {}
      showToast('템플릿을 초안으로 복제했습니다.', 'success')
      await Promise.resolve(onChanged?.())
      const nextRows = await loadAll({ keepSelection: true })
      const selected = nextRows.find((row) => String(row.id) === String(cloned.id))
      if (selected) selectTemplate(selected)
    } catch (e) {
      const msg = getApiErrorMessage(e, '템플릿 복제에 실패했습니다.')
      setError(msg)
      showToast(msg, 'error')
    } finally {
      setSaving(false)
    }
  }

  const deleteTemplate = async () => {
    if (!editingId) return
    if (!window.confirm('선택한 템플릿을 삭제할까요?')) return
    setSaving(true)
    setError('')
    try {
      await api.delete(`/customer-management/customer-memo-templates/${editingId}`, { __silent: true })
      showToast('템플릿이 삭제되었습니다.', 'success')
      clearEditor()
      await Promise.resolve(onChanged?.())
      await loadAll({ keepSelection: false })
    } catch (e) {
      const msg = getApiErrorMessage(e, '템플릿 삭제에 실패했습니다.')
      setError(msg)
      showToast(msg, 'error')
    } finally {
      setSaving(false)
    }
  }

  const resetCategoryEditor = () => {
    setCategoryEditingId(null)
    setCategoryName('')
    setCategoryOrder('0')
    setCategoryActive(true)
  }

  const selectCategory = (row) => {
    setCategoryEditingId(row.id)
    setCategoryName(row.name || '')
    setCategoryOrder(String(row.sortOrder ?? 0))
    setCategoryActive(row.active !== false)
  }

  const saveCategory = async () => {
    if (!categoryName.trim()) {
      showToast('카테고리명을 입력해 주세요.', 'error')
      return
    }
    setSaving(true)
    try {
      const body = { name: categoryName.trim(), active: categoryActive, sortOrder: Number(categoryOrder) || 0 }
      if (categoryEditingId) await api.patch(`/customer-management/customer-memo-template-categories/${categoryEditingId}`, body, { __silent: true })
      else await api.post('/customer-management/customer-memo-template-categories', body, { __silent: true })
      showToast('카테고리가 저장되었습니다.', 'success')
      resetCategoryEditor()
      await loadCategories()
    } catch (e) {
      showToast(getApiErrorMessage(e, '카테고리 저장에 실패했습니다.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const deleteCategory = async () => {
    if (!categoryEditingId) return
    if (!window.confirm('선택한 카테고리를 숨길까요?')) return
    setSaving(true)
    try {
      await api.delete(`/customer-management/customer-memo-template-categories/${categoryEditingId}`, { __silent: true })
      showToast('카테고리를 숨김 처리했습니다.', 'success')
      resetCategoryEditor()
      await loadCategories()
    } catch (e) {
      showToast(getApiErrorMessage(e, '카테고리 삭제에 실패했습니다.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null

  const isPage = variant === 'page'
  const selectedStats = selectedRow ? statsById.get(String(selectedRow.id)) : null
  const panel = (
    <div
      className={`pointer-events-auto flex w-full flex-col overflow-hidden rounded-2xl border bg-white ${isPage ? 'min-h-[760px] shadow-sm' : 'max-h-[92vh] shadow-2xl'}`}
      role={isPage ? undefined : 'dialog'}
      aria-modal={isPage ? undefined : true}
      aria-labelledby="customer-memo-template-manager-title"
      data-testid="customer-memo-template-manager"
    >
      <div className="flex shrink-0 items-center justify-between gap-3 border-b px-4 py-3 sm:px-5">
        <div id="customer-memo-template-manager-title" className="text-base font-bold text-slate-900">고객메모 템플릿 관리</div>
        <div className="flex gap-2">
          <button type="button" className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50" disabled={saving} data-testid="customer-memo-template-new" onClick={clearEditor}>새 템플릿</button>
          {showClose ? <button type="button" className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50" disabled={saving} onClick={onClose}>닫기</button> : null}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
        {error ? <div className="mb-3 rounded border border-rose-100 bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</div> : null}
        <div className="grid gap-4 xl:grid-cols-[minmax(0,340px)_minmax(0,1fr)_minmax(280px,360px)]">
          <div className="min-w-0 rounded-xl border border-slate-200 bg-slate-50/70 p-2">
            <div className="mb-2 grid gap-2">
              <input className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-xs" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="검색" />
              <div className="grid grid-cols-2 gap-2">
                <select className="min-w-0 rounded border border-slate-300 bg-white px-2 py-2 text-xs" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="">전체 상태</option>
                  {STATUS_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
                <select className="min-w-0 rounded border border-slate-300 bg-white px-2 py-2 text-xs" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                  <option value="">전체 분류</option>
                  {categoryOptions.map((item) => <option key={item} value={item}>{item}</option>)}
                </select>
              </div>
            </div>
            {loading && rows.length === 0 ? (
              <p className="px-3 py-4 text-xs text-slate-500">불러오는 중...</p>
            ) : visibleRows.length === 0 ? (
              <p className="px-3 py-4 text-xs text-slate-500">등록된 템플릿이 없습니다.</p>
            ) : (
              <div className="space-y-1">
                {visibleRows.map((template) => {
                  const selected = String(template.id || '') === String(editingId || '')
                  const rowStatus = normalizeStatus(template.status, template.active)
                  const rowStats = statsById.get(String(template.id))
                  return (
                    <button key={template.id || `${template.category}:${template.title}`} type="button" className={`block w-full rounded-lg px-3 py-2 text-left transition-colors ${selected ? 'bg-white shadow-sm ring-1 ring-brand-navy/20' : 'hover:bg-white'}`} disabled={saving} onClick={() => selectTemplate(template)}>
                      <span className="flex min-w-0 items-center gap-1 text-xs font-semibold text-slate-900">
                        <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] ${rowStatus === 'PUBLISHED' ? 'bg-emerald-50 text-emerald-700' : rowStatus === 'DRAFT' ? 'bg-amber-50 text-amber-700' : 'bg-slate-200 text-slate-600'}`}>{labelOf(STATUS_OPTIONS, rowStatus)}</span>
                        <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">{template.scopeLabel || labelOf(SCOPE_OPTIONS, normalizeScope(template.scopeType))}</span>
                        {template.category ? <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">{template.category}</span> : null}
                        <span className="truncate">{template.title || '새 템플릿'}</span>
                      </span>
                      <span className="mt-1 block truncate text-[11px] text-slate-500">{template.summary || template.plainText || '-'}</span>
                      <span className="mt-1 block text-[10px] text-slate-400">v{template.version || 1} · 사용 {(rowStats?.totalEvents || template.usageCount || 0).toLocaleString('ko-KR')} · {formatDate(rowStats?.lastUsedAt || template.lastUsedAt || template.updatedAt)}</span>
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          <div className="min-w-0 space-y-3 rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
            <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_7rem_7rem_7rem]">
              <input type="text" data-testid="customer-memo-template-title" className="min-w-0 rounded border border-slate-300 px-3 py-2 text-sm" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={255} placeholder="제목" disabled={saving} />
              <input type="number" className="min-w-0 rounded border border-slate-300 px-3 py-2 text-sm" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} placeholder="순서" disabled={saving} />
              <select className="min-w-0 rounded border border-slate-300 px-3 py-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)} disabled={saving} aria-label="상태">
                {STATUS_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
              <select className="min-w-0 rounded border border-slate-300 px-3 py-2 text-sm" value={scopeType} onChange={(e) => setScopeType(e.target.value)} disabled={saving} aria-label="범위">
                {SCOPE_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
            </div>
            {scopeType === 'ORG' ? <input type="number" className="w-full rounded border border-slate-300 px-3 py-2 text-sm" value={scopeOrgId} onChange={(e) => setScopeOrgId(e.target.value)} placeholder="조직 ID 미입력 시 내 조직" disabled={saving} /> : null}
            <input type="text" data-testid="customer-memo-template-summary" className="w-full rounded border border-slate-300 px-3 py-2 text-sm" value={summary} onChange={(e) => setSummary(e.target.value)} maxLength={500} placeholder="요약" disabled={saving} />
            <div className="grid gap-2 sm:grid-cols-2">
              <input list="customer-memo-template-category-options" type="text" data-testid="customer-memo-template-category" className="min-w-0 rounded border border-slate-300 px-3 py-2 text-sm" value={category} onChange={(e) => setCategory(e.target.value)} maxLength={50} placeholder="분류" disabled={saving} />
              <input type="text" data-testid="customer-memo-template-tags" className="min-w-0 rounded border border-slate-300 px-3 py-2 text-sm" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="태그" disabled={saving} />
              <datalist id="customer-memo-template-category-options">
                {categoryOptions.map((item) => <option key={item} value={item} />)}
              </datalist>
            </div>
            <div className="flex flex-wrap items-center gap-1 text-[11px] text-slate-500">
              <span className="font-semibold text-slate-600">변수</span>
              {CUSTOMER_MEMO_TEMPLATE_VARIABLE_TOKENS.map((token) => <button key={token} type="button" className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] text-slate-600" onClick={() => { setPlainText((prev) => `${prev || ''}${prev ? ' ' : ''}${token}`); setContent(null) }}>{token}</button>)}
            </div>
            {variableAnalysis.warnings.length > 0 ? (
              <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {variableAnalysis.warnings.slice(0, 3).map((msg) => <div key={msg}>{msg}</div>)}
              </div>
            ) : null}
            <CustomerMemoRichEditor valueHtml={memoContentToHtml(content, plainText)} plainText={plainText} attachments={[]} disabled={saving} allowAttachments={false} placeholder="템플릿 내용을 입력해 주세요." onChange={(next) => { setPlainText(next.plainText); setContent(next.content) }} />
            <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500">
              <span>v{selectedRow?.version || 1} · 사용 {(selectedStats?.totalEvents || selectedRow?.usageCount || 0).toLocaleString('ko-KR')} · 최근 {formatDate(selectedStats?.lastUsedAt || selectedRow?.lastUsedAt)}</span>
              <span>{plainText.length.toLocaleString('ko-KR')} / 50,000</span>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              {editingId ? <button type="button" className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50" disabled={saving} onClick={cloneTemplate}>복제</button> : null}
              {editingId ? <button type="button" className="rounded border border-rose-200 bg-white px-3 py-1.5 text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50" disabled={saving} onClick={deleteTemplate}>삭제</button> : null}
              <button type="button" className="rounded bg-brand-navy px-4 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50" disabled={saving} data-testid="customer-memo-template-save" onClick={saveTemplate}>{saving ? '저장 중' : '저장'}</button>
            </div>
          </div>

          <div className="min-w-0 space-y-3">
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="mb-2 text-sm font-bold text-slate-900">미리보기</div>
              <div className="mb-2 grid gap-2 sm:grid-cols-3 xl:grid-cols-1">
                <input className="rounded border border-slate-300 px-2 py-1.5 text-xs" value={previewCustomer} onChange={(e) => setPreviewCustomer(e.target.value)} placeholder="샘플 고객명" />
                <input className="rounded border border-slate-300 px-2 py-1.5 text-xs" value={previewPhone} onChange={(e) => setPreviewPhone(e.target.value)} placeholder="샘플 연락처" />
                <input className="rounded border border-slate-300 px-2 py-1.5 text-xs" value={previewUser} onChange={(e) => setPreviewUser(e.target.value)} placeholder="샘플 담당자" />
              </div>
              {previewTemplate.variableWarnings?.length > 0 ? <div className="mb-2 rounded bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">{previewTemplate.variableWarnings[0]}</div> : null}
              <div className="rounded border border-slate-200 bg-slate-50 p-3">
                <div className="mb-2 text-xs font-bold text-slate-800">{previewTemplate.title || '제목 없음'}</div>
                <div className="max-h-64 overflow-auto rounded bg-white p-2 text-xs leading-5 text-slate-700 whitespace-pre-wrap">{previewTemplate.plainText || '-'}</div>
              </div>
            </div>

            {memoOps ? (
              <div className="rounded-xl border border-slate-200 bg-white p-3">
                <div className="mb-2 text-sm font-bold text-slate-900">운영 현황</div>
                <div className="grid grid-cols-2 gap-2 text-center text-xs">
                  <div className="rounded bg-slate-50 p-2"><div className="font-bold text-slate-900">{Number(memoOps.memoCount || 0).toLocaleString('ko-KR')}</div><div className="text-[10px] text-slate-500">메모</div></div>
                  <div className="rounded bg-slate-50 p-2"><div className="font-bold text-slate-900">{Number(memoOps.attachmentCount || 0).toLocaleString('ko-KR')}</div><div className="text-[10px] text-slate-500">첨부</div></div>
                  <div className="rounded bg-slate-50 p-2"><div className="font-bold text-slate-900">{formatBytes(memoOps.totalAttachmentBytes)}</div><div className="text-[10px] text-slate-500">메모 용량</div></div>
                  <div className="rounded bg-slate-50 p-2"><div className="font-bold text-slate-900">{Number(memoOps.unlinkedCustomerMemoFiles || 0).toLocaleString('ko-KR')}</div><div className="text-[10px] text-slate-500">미연결</div></div>
                </div>
                {Number(memoOps.oldUnlinkedCustomerMemoFiles || 0) > 0 ? <div className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">{Number(memoOps.unlinkedStaleDays || 7).toLocaleString('ko-KR')}일 이상 미연결 업로드 {Number(memoOps.oldUnlinkedCustomerMemoFiles || 0).toLocaleString('ko-KR')}건</div> : null}
              </div>
            ) : null}
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="mb-2 text-sm font-bold text-slate-900">사용 통계</div>
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="rounded bg-slate-50 p-2"><div className="font-bold text-slate-900">{(selectedStats?.totalEvents || 0).toLocaleString('ko-KR')}</div><div className="text-[10px] text-slate-500">적용</div></div>
                <div className="rounded bg-slate-50 p-2"><div className="font-bold text-slate-900">{(selectedStats?.uniqueUsers || 0).toLocaleString('ko-KR')}</div><div className="text-[10px] text-slate-500">사용자</div></div>
                <div className="rounded bg-slate-50 p-2"><div className="font-bold text-slate-900">{(selectedStats?.uniqueCustomers || 0).toLocaleString('ko-KR')}</div><div className="text-[10px] text-slate-500">고객</div></div>
              </div>
              <div className="mt-2 max-h-40 overflow-auto space-y-1 text-[11px] text-slate-600">
                {(usage.recent || []).slice(0, 8).map((event) => <div key={event.id} className="truncate rounded bg-slate-50 px-2 py-1">{formatDate(event.createdAt)} · {event.templateTitle || '-'} · {event.actorName || event.actorEmail || '-'}</div>)}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="text-sm font-bold text-slate-900">카테고리</div>
                <button type="button" className="rounded border px-2 py-1 text-[11px]" onClick={resetCategoryEditor}>새로</button>
              </div>
              <div className="grid gap-2">
                <input className="rounded border border-slate-300 px-2 py-1.5 text-xs" value={categoryName} onChange={(e) => setCategoryName(e.target.value)} placeholder="카테고리명" />
                <div className="grid grid-cols-[1fr_auto] gap-2">
                  <input type="number" className="rounded border border-slate-300 px-2 py-1.5 text-xs" value={categoryOrder} onChange={(e) => setCategoryOrder(e.target.value)} placeholder="순서" />
                  <label className="flex items-center gap-1 text-xs text-slate-600"><input type="checkbox" checked={categoryActive} onChange={(e) => setCategoryActive(e.target.checked)} />사용</label>
                </div>
                <div className="flex justify-end gap-2">
                  {categoryEditingId ? <button type="button" className="rounded border border-rose-200 px-2 py-1 text-xs text-rose-700" disabled={saving} onClick={deleteCategory}>숨김</button> : null}
                  <button type="button" className="rounded bg-slate-900 px-3 py-1 text-xs font-semibold text-white" disabled={saving} onClick={saveCategory}>저장</button>
                </div>
              </div>
              <div className="mt-2 max-h-40 overflow-auto space-y-1">
                {categories.map((row) => <button key={row.id} type="button" className={`block w-full rounded px-2 py-1 text-left text-xs ${String(row.id) === String(categoryEditingId) ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-700 hover:bg-slate-100'}`} onClick={() => selectCategory(row)}>{row.name} {row.active === false ? '(숨김)' : ''}</button>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  if (isPage) return <div className="w-full">{panel}</div>
  return (
    <div className="fixed inset-0 z-[72]">
      <button type="button" className="absolute inset-0 bg-black/45" disabled={saving} onClick={() => { if (!saving) onClose?.() }} aria-label="닫기" />
      <div className="relative mx-auto flex h-full max-w-7xl items-end px-3 py-6 sm:items-center sm:px-5">{panel}</div>
    </div>
  )
}